import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import panzoom, { PanZoom } from 'panzoom';
import { interval, Observable } from 'rxjs';
import { take } from 'rxjs/internal/operators/take';
import { FlowModeState } from '../../../../shared/models/state/Flow';
import { selectFlowModeState } from '../../../../core/state/selectors/flow.selector';
import {swalWarningBottom, swalWarningTop} from '../../../../shared/utils/utils';

interface PreviousZoom {
  x: number;
  y: number;
  zoomPercentage: number;
}

const panzoomOptions = {
  maxZoom: 2,
  minZoom: 0.1,
  zoomDoubleClickSpeed: 1,
  zoomSpeed: 0.08,
  smoothScroll: true,
  transformOrigin: {x: 0.5, y: 0.5}
};

export type ZoomType = 'panZoom' | 'scrollZoom';

@Injectable()
export class ZoomService {
  private area: any;
  private zoomPercentage = 100;
  private zoomType: ZoomType;
  private panZoomInstance: PanZoom;
  private previousZoom: PreviousZoom;
  private isZoomOnClick: Boolean = false;
  private flowState$:Observable<FlowModeState> = this.store.select(selectFlowModeState);
  private flowState: FlowModeState;

  constructor(private store:Store) {}

  set setPanZoomInstance(area: HTMLDivElement) {
    this.area = area;
    this.panZoomInstance = panzoom(area, panzoomOptions);
    this.panZoomInstance.on('zoom', () => {
      if (event) {
        this.isZoomOnClick = event.type === 'click';
      }
      if (this.zoomType === 'panZoom' && !this.isZoomOnClick) {
        const getScale = Math.trunc(
          this.area.style.transform.replace('matrix(', '').split(',')[0] * 100
        );
        this.zoomPercentage = getScale;
      }
    });
  }

  get getZoomPercentage() {
    return this.zoomPercentage;
  }

  setZoomType(zoomType: ZoomType) {
    this.zoomType = zoomType;
    switch (zoomType) {
      case 'panZoom':
        this.resetPanZoomView();
        break;
      case 'scrollZoom':
        this.resetScrollView();
        break;
    }
  }

  public isPanZoom() {
    return !this.panZoomInstance.isPaused();
  }

  private resetPanZoomView() {
    this.panZoomInstance.resume();
    this.panZoomInstance.moveTo(0, 0);
    this.panZoomInstance.zoomAbs(0, 0, 1);
    this.setZoom(100);
  }

  private resetScrollView() {
    this.panZoomInstance.pause();
    const dragArea = document.getElementById('dragArea');
    this.setZoom(100);
    if (dragArea) {
      dragArea.scroll(0, 0);
    }
  }

  private changeZoomPercentage(zoomValue: number) {
    const newZoomPercentage = zoomValue / 100;
    if (this.panZoomInstance) {
      if (newZoomPercentage <= panzoomOptions.maxZoom) {
        if (newZoomPercentage >= panzoomOptions.minZoom) {
          this.panZoomInstance.zoomAbs(
            this.panZoomInstance.getTransform().x,
            this.panZoomInstance.getTransform().y,
            newZoomPercentage
          );
        } else {
          // this.zoomPrecentageDisplay.emit(panzoomOptions.minZoom * 100);
          this.zoomPercentage = panzoomOptions.minZoom * 100;
        }
      } else {
        // this.zoomPrecentageDisplay.emit(panzoomOptions.maxZoom * 100);
        this.zoomPercentage = panzoomOptions.maxZoom * 100;
      }
    }
  }

  increaseZoom() {
    this.zoomPercentage += 10
    this.changeZoomPercentage(this.zoomPercentage);
  }

  decreaseZoom() {
    this.zoomPercentage -= 10
    this.changeZoomPercentage(this.zoomPercentage);
  }

  setZoom(value: number) {
    this.changeZoomPercentage(value);
    this.zoomPercentage = value;
  }

  resetZoom() {
    this.setZoomType(this.zoomType);
  }

  public zoomToFit(drawArea: HTMLDivElement, dragArea: HTMLDivElement) {
    this.previousZoomData(dragArea);
    const drawAreaWidth = drawArea.scrollWidth;
    const drawAreaHeight = drawArea.scrollHeight;
    const clientRect = dragArea.getBoundingClientRect();
    let flowAreaWidth = clientRect.width;
    const flowAreaHeight = clientRect.height - clientRect.top;
    const widthDiff = flowAreaWidth - drawAreaWidth;
    const heightDiff = flowAreaHeight - drawAreaHeight;
    let isWidthGreater = false;
    if (this.zoomType === 'panZoom') {
      const rightSidebarWidth = document
        .getElementById('right-sidebar-parent')
        .getBoundingClientRect().width;
      const leftSidebarWidth = document
        .getElementById('leftSidebarContent')
        .getBoundingClientRect().width;
      flowAreaWidth = flowAreaWidth - rightSidebarWidth - leftSidebarWidth - 5;
    }
    if (Math.abs(widthDiff) > Math.abs(heightDiff)) {
      isWidthGreater = true;
    }
    let zoomPercentage = 0;
    if (isWidthGreater) {
      zoomPercentage = (flowAreaWidth / drawAreaWidth) * 100;
    } else {
      zoomPercentage = (flowAreaHeight / drawAreaHeight) * 100;
    }
    if (zoomPercentage > panzoomOptions.maxZoom * 100) {
      zoomPercentage = panzoomOptions.maxZoom * 100;
    }
    if (zoomPercentage < panzoomOptions.minZoom * 100) {
      zoomPercentage = panzoomOptions.minZoom * 100;
    }
    this.resetZoom();
    this.setZoom(Math.floor(zoomPercentage));
  }

  private previousZoomData(dragArea: HTMLDivElement) {
    if (this.isPanZoom()) {
      this.previousZoom = {
        x: this.panZoomInstance.getTransform().x,
        y: this.panZoomInstance.getTransform().y,
        zoomPercentage: this.zoomPercentage ? this.zoomPercentage : 0,
      };
    } else {
      this.previousZoom = {
        x: dragArea.scrollLeft,
        y: dragArea.scrollTop,
        zoomPercentage: this.zoomPercentage ? this.zoomPercentage : 0,
      };
    }
  }

  public restorePreviousZoomPercentage(dragArea: HTMLDivElement, stepId: string) {
    if (this.isPanZoom()) {
      if (stepId) {
        this.scrollTo(stepId);
        this.setZoom(100);
      } else {
        this.setZoom(this.previousZoom.zoomPercentage);
        this.panZoomInstance.moveTo(this.previousZoom.x, this.previousZoom.y);
      }
    } else {
      this.zoomPercentage = this.previousZoom.zoomPercentage;
      this.changeZoomPercentage(this.previousZoom.zoomPercentage);
      if (stepId) {
        const count = interval(1000);
        const takeOneNumbers = count.pipe(take(1));
        dragArea.style.scrollBehavior = 'unset';
        this.scrollTo(stepId);
        takeOneNumbers.toPromise().then(() => {
          dragArea.style.scrollBehavior = 'smooth';
        });
      } else {
        setTimeout(() => {
          dragArea.scroll(this.previousZoom.x, this.previousZoom.y);
        }, 5);
      }
    }
  }

  public scrollTo(id: string, area?: string) {
    if (id) {
      if (this.zoomType === 'panZoom') {
        if (this.panZoomInstance) {
          const node = document.getElementById(id);
          if (node) {
            this.area.style.transition = '700ms';
            const count = interval(1000);
            const takeOneNumbers = count.pipe(take(1));
            this.panZoomInstance.zoomAbs(0, 0, 1);

            const top = +node.dataset.top;
            const left = +node.dataset.left;
            if(this.flowState === FlowModeState.PREVIEW_MODE) {
              let size = this.getCenterArea(-left, -top);
              this.panZoomInstance.moveTo(size.leftVal, size.topVal);
            } else {
              if (area) {
                this.panZoomInstance.moveTo(-left/2, -top/2);
                return
              }
              this.panZoomInstance.moveTo(-left, -top);
            }

            takeOneNumbers.toPromise().then(() => {
              this.area.style.transition = 'none';
            });
          } else {
            this.stepNotOnCanvasWarning(id);
          }
        } else {
          console.error('ScrollTo - panzoom not found');
        }
      } else {
        const node = document.getElementById(id);
        const dragArea = document.getElementById(area ? area : 'dragArea');
        if (node) {
         const top = +node.dataset.top;
          const left = +node.dataset.left;
          this.zoomPercentage = 100;
          this.changeZoomPercentage(100);
          this.flowState$.subscribe((flow) => {
            this.flowState = flow;
            if (this.flowState === FlowModeState.PREVIEW_MODE) {
              const positions = this.getCenterArea(left, top);
              dragArea.scroll(positions.leftVal, positions.topVal);
            } else {
              node.scrollIntoView({behavior: "smooth", block: "center"})
            }
          });
        } else {
          this.stepNotOnCanvasWarning(id);
        }
      }
    }
  }


  /**
   * It takes in a left and top value, and returns a new left and top value that is the center of the
   * area
   * @param {number} left - the left position of the element
   * @param {number} top - the top position of the element
   * @returns An object with two properties, left and top.
   */
  getCenterArea(left: number, top: number) {
    if (this.zoomType !== 'panZoom') {
      return this.calculatePosition(left, top, 80);
    } else {
      return this.calculatePosition(left, top, -80);
    }
  }

  calculatePosition(left: number, top: number, transitionValue) {
    const leftVal = left === 0 ? left : left + transitionValue;
    const topVal = top === 0 ? top : top + transitionValue;
    return {leftVal, topVal}
  }

  private stepNotOnCanvasWarning(stepId: string) {
    swalWarningTop({ title: `${stepId}: This step is not in the active flow, please check the deleted nodes.` });
  }
}
